import { RefreshCw, Save } from 'lucide-react'
import { useId } from 'react'

import { ActionInfo, Button, StatusMessage } from '@krp/ui-kit'

import type { Schedule, ShownError } from '../model/types'
import { ErrorBlock } from './parts'
import './sync.css'

export interface ScheduleVm {
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  schedule: Schedule | null
  draft: string
  busy: boolean
  error: ShownError | null
  saved: boolean
}

export interface ScheduleActions {
  draftChanged: (time: string) => void
  save: () => void
  reload: () => void
}

/**
 * Время ночной полной сверки (требования 13): раз в сутки, вне часов кассы. Сверка читает все источники постранично и
 * пересчитывает расхождения; сама ничего не применяет.
 */
export function ScheduleScreen({ vm, actions }: { vm: ScheduleVm; actions: ScheduleActions }) {
  const inputId = useId()
  const schedule = vm.schedule
  const invalid = /^\d{2}:\d{2}$/.test(vm.draft) ? null : 'Укажите время в виде ЧЧ:ММ.'
  const same = schedule !== null && schedule.nightlyAt === vm.draft ? 'Время не менялось.' : null
  return (
    <section aria-labelledby="sync-schedule-title" className="sync">
      <header>
        <h2 id="sync-schedule-title">Ночная сверка</h2>
        <p className="sync__lead">Раз в сутки система читает все источники целиком и находит все расхождения. Выберите время, когда касса не работает.</p>
      </header>

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Загружаем расписание…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="sync.schedule.reload">
              Повторить
            </Button>
          }
        />
      )}
      {vm.status === 'ready' && schedule !== null && (
        <>
          <div className="sync-time">
            <ActionInfo id="sync.schedule.time" label="Время сверки">
              <label htmlFor={inputId}>Время сверки (часовой пояс {schedule.timeZone})</label>
            </ActionInfo>
            <input id={inputId} type="time" value={vm.draft} onChange={(event) => actions.draftChanged(event.target.value)} />
          </div>
          <div className="sync__row">
            <Button
              variant="primary"
              icon={<Save size={16} aria-hidden="true" />}
              busy={vm.busy}
              disabled={invalid !== null || same !== null}
              disabledReason={invalid ?? same}
              onClick={actions.save}
              actionId="sync.schedule.save"
            >
              Сохранить
            </Button>
            {vm.saved && same !== null && (
              <p className="sync__note" role="status">
                Время сохранено: следующая сверка пройдёт в {schedule.nightlyAt}.
              </p>
            )}
          </div>
          {vm.error !== null && <ErrorBlock error={vm.error} />}
        </>
      )}
    </section>
  )
}
